import type { MetricsClient } from '@strata-monorepo/shared-types'
import { REQUEST_TRACE_ENDPOINTS, REQUEST_TRACE_EVENT, REQUEST_TRACE_OUTCOMES } from './constants'
import type { RequestTrace, RequestTraceName, RequestTracePhase, RequestTraceStep } from './types'

type TraceValue = string | number | boolean

// One id per page load, so every event from a visit can be grouped.
const traceId = Math.random().toString(36).slice(2, 10)

function sendTraceEvent(
  metricsClient: MetricsClient,
  request: RequestTraceName,
  phase: RequestTracePhase,
  durationMs?: number,
): void {
  try {
    const step: RequestTraceStep = `${request}.${phase}`
    const attributes: Record<string, TraceValue> = {
      request,
      endpoint: REQUEST_TRACE_ENDPOINTS[request],
      step,
      outcome: REQUEST_TRACE_OUTCOMES[phase],
      traceId,
      cohesionSessionId: String(window._Cohesion?.sessionId ?? ''),
      cohesionAnonymousId: String(window._Cohesion?.anonymousId ?? ''),
      elapsedMs: Math.round(performance.now()),
    }
    if (durationMs !== undefined) attributes.durationMs = durationMs

    metricsClient.trackEvent(REQUEST_TRACE_EVENT, attributes)
  } catch {
    // Observability must not become a new failure mode for the request it observes.
  }
}

/**
 * Starts tracing one visitor-facing request and sends `<request>.start`. Call `succeed`, `fail` or
 * `invalid` once the request has settled, after any retries, so New Relic counts outcomes rather than
 * attempts. The helper measures `durationMs` itself; callers pass no payload. Callers hand over
 * the metrics client they read from the store at call time, and tracing never throws.
 */
export function traceRequest(
  metricsClient: MetricsClient,
  request: RequestTraceName,
): RequestTrace {
  const startedAt = performance.now()
  let settled = false
  sendTraceEvent(metricsClient, request, 'start')

  const settle = (phase: 'success' | 'error' | 'invalid'): void => {
    if (settled) return
    settled = true
    sendTraceEvent(metricsClient, request, phase, Math.round(performance.now() - startedAt))
  }

  return {
    succeed: () => settle('success'),
    fail: () => settle('error'),
    invalid: () => settle('invalid'),
  }
}

// Apollo reports these two link errors as `networkError` even when the server answered 2xx.
const UNUSABLE_BODY_ERROR_NAMES = ['ServerParseError', 'ServerError']

function isUnusableSuccessBody(networkError: unknown): boolean {
  if (typeof networkError !== 'object' || networkError === null) return false
  const { name, statusCode } = networkError as { name?: unknown; statusCode?: unknown }
  return (
    typeof name === 'string' &&
    UNUSABLE_BODY_ERROR_NAMES.includes(name) &&
    typeof statusCode === 'number' &&
    statusCode >= 200 &&
    statusCode < 300
  )
}

/**
 * True when a request came back but the response is unusable, as opposed to failing on the network.
 * Apollo throws for a 200 response with `errors`, and as a `networkError` for a 2xx body that is not
 * JSON or has neither `data` nor `errors`. Apollo sorts these by where they happen, not by status
 * code, so a 2xx `networkError` of those types still means a response arrived.
 */
export function isGraphQlErrorResponse(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const { graphQLErrors, networkError } = error as {
    graphQLErrors?: unknown
    networkError?: unknown
  }
  if (networkError) return isUnusableSuccessBody(networkError)
  return Array.isArray(graphQLErrors) && graphQLErrors.length > 0
}
